import { RouteErrorScreen } from '@components/AppErrorScreen';
import { PageLayout } from '@components/PageLayout';
import ProtectedRoute from '@components/protectedRoute';
import { RoleGuard } from '@components/RoleGuard';
import AccountDetail from '@pages/accounts/AccountDetail';
import { Accounts } from '@pages/accounts/Accounts';
import { BatchDetailPage } from '@pages/batch-operations/BatchDetailPage.tsx';
import { BatchOperations } from '@pages/batch-operations/BatchOperations.tsx';
import { DashboardView } from '@pages/dashboard';
import Messages from '@pages/messages/messages';
import { MessagesLayout } from '@pages/messages/MessagesLayout';
import { NewMessage } from '@pages/messages/NewMessage';
import Threads from '@pages/messages/Threads';
import OperationsPage from '@pages/operations/OperationsPage.tsx';
import { SignPaymentsPage } from '@pages/operations/SignPaymentsPage.tsx';
import { PaymentsPage } from '@pages/payments';
import { BatchUpload } from '@pages/payments/batch/BatchUpload';
import { FXForm } from '@pages/payments/fx';
import { BicValidationProvider } from '@pages/payments/fx/contexts/BicValidationContext';
import { OperationLoader } from '@pages/payments/OperationLoader';
import { Orders } from '@pages/payments/orders';
import { SepaPaymentForm } from '@pages/payments/sepa/SepaPaymentForm';
import { SpecificPayment } from '@pages/payments/SpecificPayment';
import { SwiftPaymentForm } from '@pages/payments/swift/SwiftPaymentForm';
import { TemplateLoader } from '@pages/payments/TemplateLoader';
import {
	LegalDocuments,
	LoginActivity,
	Profile,
	PushNotifications,
	SettingsPage,
} from '@pages/settings';
import DownloadStatementsView from '@pages/statements/components/DownloadStatementsView';
import GenerateStatementsView from '@pages/statements/components/GenerateStatementsView';
import Statements from '@pages/statements/statements';
import { Support } from '@pages/support';
import { TemplatesForm, TemplatesListPage } from '@pages/templates';
import { TemplateEditLoader } from '@pages/templates/TemplateEditLoader';
import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Capability } from 'types/roles';

export const routes: RouteObject[] = [
	{
		path: '/',
		element: (
			<ProtectedRoute>
				<PageLayout />
			</ProtectedRoute>
		),
		errorElement: <RouteErrorScreen />,
		children: [
			{ index: true, element: <Navigate to="dashboard" /> },

			{
				path: 'dashboard',
				element: <DashboardView />,
			},

			{ path: 'accounts', element: <Accounts /> },
			{ path: 'accounts/:uuid', element: <AccountDetail /> },

			{
				path: 'statements',
				element: <Statements />,
				children: [
					{ index: true, element: <Navigate replace to="generate" /> },
					{ path: 'generate', element: <GenerateStatementsView /> },
					{ path: 'download', element: <DownloadStatementsView /> },
					{ path: '*', element: <Navigate to={'/statements'} /> },
				],
			},

			{
				path: 'payments',
				element: <PaymentsPage />,
			},
			{
				path: 'payments/batch',
				element: (
					<RoleGuard capability={Capability.CREATE_PAYMENT} fallbackRoute="/payments">
						<BatchUpload />
					</RoleGuard>
				),
			},

			{
				path: 'payments',
				element: (
					<RoleGuard capability={Capability.CREATE_PAYMENT} fallbackRoute="/payments">
						<Orders />
					</RoleGuard>
				),
				children: [
					{
						path: 'sepa',
						element: (
							<TemplateLoader>
								<SepaPaymentForm />
							</TemplateLoader>
						),
					},
					{
						path: 'fx',
						element: (
							<BicValidationProvider>
								<FXForm />
							</BicValidationProvider>
						),
					},
					{
						path: 'international',
						element: (
							<TemplateLoader>
								<SwiftPaymentForm />
							</TemplateLoader>
						),
					},
					// TODO Remove completly with FF task and delete old batches
					// The path was adjusted because we can't really replace the page with an outlet inside other page, without facing layout breakage.
					{
						path: 'batch-upload',
						element: <Navigate replace to="/payments/batch" />,
					},
					{
						path: 'sepa/:id',
						element: (
							<OperationLoader>
								<SepaPaymentForm />
							</OperationLoader>
						),
					},
					{
						path: 'fx/:id',
						element: (
							<BicValidationProvider>
								<FXForm />
							</BicValidationProvider>
						),
					},
					{
						path: 'international/:id',
						element: (
							<OperationLoader>
								<SwiftPaymentForm />
							</OperationLoader>
						),
					},
					{ path: '*', element: <Navigate to={'/payments'} /> },
				],
			},
			{
				path: 'payments/:id',
				element: (
					<OperationLoader>
						<SpecificPayment />
					</OperationLoader>
				),
			},
			{
				path: 'operations',
				children: [
					{
						index: true,
						element: <OperationsPage />,
					},

					{
						path: 'sign',
						element: <SignPaymentsPage />,
					},

					{
						path: 'batch',
						children: [
							{
								index: true,
								element: <BatchOperations />,
							},
							{
								path: ':batchId',
								element: <BatchDetailPage />,
							},
							{
								path: '*',
								element: <Navigate replace to="/operations/batch" />,
							},
						],
					},

					{
						path: '*',
						element: <Navigate replace to="/operations" />,
					},
				],
			},

			{ path: 'messages/:id', element: <Threads /> },

			{
				path: 'templates',
				children: [
					{ index: true, element: <TemplatesListPage /> },
					{
						path: 'new',
						element: (
							<RoleGuard
								capability={Capability.MANAGE_TEMPLATE}
								fallbackRoute="/templates"
							>
								<TemplatesForm />
							</RoleGuard>
						),
					},
					{
						path: ':templateId',
						element: (
							<RoleGuard
								capability={Capability.MANAGE_TEMPLATE}
								fallbackRoute="/templates"
							>
								<TemplateEditLoader />
							</RoleGuard>
						),
					},
				],
			},

			{
				path: 'messages',
				element: <MessagesLayout />,
				children: [
					{ index: true, element: <Messages /> },
					{ path: 'new', element: <NewMessage /> },
					{ path: '*', element: <Navigate to={'/messages'} /> },
				],
			},

			{
				path: 'settings',
				children: [
					{ index: true, element: <SettingsPage /> },
					{
						path: 'profile',
						element: <Profile />,
					},
					{
						path: 'legal',
						element: <LegalDocuments />,
					},
					{
						path: 'activity',
						element: <LoginActivity />,
					},
					{ path: 'push', element: <PushNotifications /> },
					{ path: '*', element: <Navigate to={'/settings'} /> },
				],
			},

			{
				path: 'support',
				element: <Support />,
			},

			{
				path: 'new-batch-operations',
				element: <OperationsPage />,
			},

			{ path: '*', element: <Navigate to="/dashboard" /> },
		],
	},
];

export const router = createBrowserRouter(routes);
